import React, { useEffect, useState } from 'react';
import { CountryCode } from '@dayetopia/library/types';
import { useLocation } from '@reach/router';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { PageSlice, ProductCode } from '@contracts';
import { getShopProducts } from '@data/storefront';
import { useCountry } from '@hooks/useCountry';
import { filterProducts, filterProductsByCountry } from '@utils/product';
import useNavigate from '@utils/useNavigate';
import Button from './shared/Button';
import Carousel from './shared/Carousel';
import Colors from './shared/Colors';
import { TextVariant, Typography } from './shared/design/Typography';
import ProductBox from './storefront/ProductBox';

interface Props {
  slice: PageSlice;
}

const ProductsShowcase: React.FC<Props> = ({ slice }) => {
  const { navigateWrapper } = useNavigate();
  const { country } = useCountry();
  const { t } = useTranslation();
  const location = useLocation();
  const setProducts = slice.primary.product_ids?.text;
  const titleKey = slice.primary.title_key;
  const [selectedProductIds, setSelectedProductIds] = useState<ProductCode[]>([]);
  const backgroundColor = slice.primary.background_color ?? Colors.cream;
  const shopProducts = getShopProducts({ isProductShowcase: true });
  const filteredProducts = filterProducts(country, shopProducts);

  const defaultProductsByRegion: Partial<Record<CountryCode, ProductCode[]>> = {
    [CountryCode.GB]: [ProductCode.VMSPremium, ProductCode.TamponBox, ProductCode.Proviotics, ProductCode.Pads],
    [CountryCode.US]: [ProductCode.VMSEssential, ProductCode.TamponBoxUS, ProductCode.Proviotics, ProductCode.Pads]
  };

  const defaultProductIds = defaultProductsByRegion[country] || [
    ProductCode.VMSEssential,
    ProductCode.TamponBox,
    ProductCode.Proviotics,
    ProductCode.Pads
  ];

  const flickityOptions = {
    initialIndex: 0,
    pageDots: false,
    prevNextButtons: false,
    setGallerySize: true,
    contain: true,
    cellAlign: 'left',
    draggable: true,
    watchCSS: true
  };

  useEffect(() => {
    if (setProducts && setProducts !== '') {
      setSelectedProductIds(filterProductsByCountry(setProducts.split(','), country));
    } else {
      setSelectedProductIds(filterProductsByCountry(defaultProductIds, country));
    }
  }, [country, location]);

  const getProductData = (productId: ProductCode) => {
    const foundByOverride = filteredProducts.find((p) => p.product_code_override?.text?.startsWith(productId));

    if (foundByOverride) {
      return foundByOverride;
    }

    return filteredProducts.find((p) => p.product_code === productId);
  };

  return (
    <Wrapper $background={backgroundColor}>
      {titleKey ? (
        <TitleWrap>
          <Typography $variant={TextVariant.Heading4} $mobileVariant={TextVariant.Heading5}>
            {t(titleKey)}
          </Typography>
        </TitleWrap>
      ) : null}
      <CarouselContainer data-testid="carousel-container">
        {selectedProductIds.length > 0 && (
          <Carousel options={flickityOptions}>
            {selectedProductIds.map((productId, i) => {
              const productData = getProductData(productId);

              if (productData) {
                return (
                  <ProductBoxWrap key={`${productId}-${i}`}>
                    <ProductBox data={productData} mobileShowDescription desktopDisplay />
                  </ProductBoxWrap>
                );
              }
              return null;
            })}
          </Carousel>
        )}
      </CarouselContainer>
      <ButtonWrap>
        <Button color={Colors.navyBlue} onClick={() => navigateWrapper('/products/')}>
          {t('components.explore-all-products')}
        </Button>
      </ButtonWrap>
    </Wrapper>
  );
};

export default ProductsShowcase;

const Wrapper = styled.div<{ $background: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  background: ${(props) => (props.$background ? props.$background : 'transparent')};
  padding: 96px 0 0;
  overflow: hidden;

  @media (max-width: 1024px) {
    gap: 32px;
    padding: 40px 16px 0;
    align-items: flex-start;
  }
`;

const TitleWrap = styled.div`
  text-align: center;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const ButtonWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  & button {
    border-radius: 5px;
  }
`;

const CarouselContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;

  .flickity-viewport {
    overflow: visible;
    transform: translateZ(0);
  }

  & .carousel {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
    gap: 24px;

    @media (max-width: 1200px) {
      flex-wrap: wrap;
      max-width: 800px;
      margin: 0 auto;
    }

    @media (max-width: 1024px) {
      max-width: 100%;
      display: block;
    }

    &:after {
      content: '';

      @media (max-width: 1024px) {
        content: 'flickity';
        display: none; /* hide :after */
      }
    }
  }
`;

const ProductBoxWrap = styled.div`
  margin: 0;

  @media (max-width: 1024px) {
    margin: 0 24px 0 0;
  }
`;
